import { RunnerStep } from '../../models/test-run.model';
import { isOpenStatus } from '../../shared/test-run-outcome';

/** Art einer Kopfzeile: Modulaufruf, Runde einer Schleife, Block Rückweg oder Runde im Rückweg. */
export type StepGroupKind = 'MODULE' | 'PASS' | 'RETURN_BLOCK' | 'RETURN_PASS';

export interface NumberedStep {
  step: RunnerStep;
  /** Anzeige-Nummer wie im Design: „1", „1.1", „1.2" (`page-testlauf.jsx:18-27`). */
  number: string;
  /** Zahl der Vorfahren in derselben Liste — steuert den Einzug. */
  depth: number;
  /** Gruppen-Kopfzeile: traegt kein eigenes Ergebnis und ist nicht anklickbar. */
  isGroup: boolean;
  /** Der Elternknoten, wenn er in derselben Liste steht. */
  parentId: string | null;
  /** Die Vorfahren in derselben Liste, von aussen nach innen. */
  ancestorIds: readonly string[];
  /** Nur an Kopfzeilen gesetzt. */
  groupKind: StepGroupKind | null;
}

function groupKindOf(step: RunnerStep): StepGroupKind | null {
  if (step.kind !== 'MODULE') return null;
  if (step.loopPhase === 'ROUND') return 'PASS';
  if (step.loopPhase === 'RETURN') return step.passIndex == null ? 'RETURN_BLOCK' : 'RETURN_PASS';
  return 'MODULE';
}

/**
 * Nummeriert die flache Schrittliste des Servers so, wie der Runner sie zeigt.
 *
 * <p>Das Backend liefert die Schritte in Ausfuehrungsreihenfolge mit einem
 * Verweis auf ihre Gruppe; die Nummer entsteht erst in der Anzeige. Ein Schritt,
 * dessen Gruppe nicht in derselben Liste steht, zaehlt auf oberster Ebene mit,
 * statt zu verschwinden.
 *
 * <p>Die Tiefe kommt aus der Elternkette, nicht aus `depth` des Servers: fuer neue Laeufe
 * ist sie gleich, Altlaeufe tragen dort ueberall 0 und behielten sonst keinen Einzug (NEX-2281).
 */
export function numberSteps(steps: readonly RunnerStep[]): NumberedStep[] {
  const ids = new Set(steps.map((step) => step.id));
  const childCounters = new Map<string, number>();
  const seen = new Map<string, NumberedStep>();
  let topLevel = 0;

  return steps.map((step) => {
    const parentId = step.parentStepResultId;
    const isGroup = step.kind === 'MODULE';
    const groupKind = groupKindOf(step);
    let entry: NumberedStep;
    if (parentId === null || !ids.has(parentId)) {
      topLevel += 1;
      entry = { step, number: String(topLevel), depth: 0, isGroup, parentId: null, ancestorIds: [], groupKind };
    } else {
      const position = (childCounters.get(parentId) ?? 0) + 1;
      childCounters.set(parentId, position);
      const parent = seen.get(parentId);
      const ancestorIds = [...(parent?.ancestorIds ?? []), parentId];
      const number = `${parent?.number ?? String(topLevel)}.${position}`;
      entry = { step, number, depth: ancestorIds.length, isGroup, parentId, ancestorIds, groupKind };
    }
    seen.set(step.id, entry);
    return entry;
  });
}

/**
 * Alles unter der Kopfzeile ist erledigt: jede bewertbare Zeile hat ein Ergebnis und kein Aufruf
 * wartet auf eine Entscheidung. Eine Gruppe ohne solche Zeilen gilt als fertig.
 */
export function isFinishedGroup(entries: readonly NumberedStep[], group: NumberedStep): boolean {
  return entries
    .filter((entry) => entry.ancestorIds.includes(group.step.id))
    .every((entry) =>
      entry.step.kind === 'MODULE' ? entry.step.callState !== 'OPEN' : !isOpenStatus(entry.step.actualStatus),
    );
}

/** Die Zeilen ohne die Nachfahren zugeklappter Kopfzeilen. */
export function visibleEntries(
  entries: readonly NumberedStep[],
  isExpanded: (group: NumberedStep) => boolean,
): NumberedStep[] {
  const collapsed = new Set(entries.filter((entry) => entry.isGroup && !isExpanded(entry)).map((e) => e.step.id));
  return entries.filter((entry) => !entry.ancestorIds.some((id) => collapsed.has(id)));
}
